import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logoSrc from "@/assets/logo_name_and_symbol.svg";
import {
  LogOut,
  LayoutDashboard,
  Plus,
  Users,
  UserSquare2,
  Settings,
  FileText,
  ClipboardList,
  LayoutList,
  NotebookTabs,
  Menu,
  X,
  User,
  ClipboardCheck,
  ShieldCheck,
  Bell,
  ClipboardSignature,
  Pill,
  Building2,
  Terminal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSession, signOut } from "@/lib/auth-client";
import { UserRole } from "@arna-med/shared";
import { useDoctorPendingCount } from "@/hooks/api/useReports";
import { getLocalizedName } from "@/lib/utils";

const appName =
  import.meta.env.VITE_APP_ENV === "test" ? "Test Arna Med" : "Arna Med";

interface LayoutProps {
  children: React.ReactNode;
}

function BellIcon() {
  const { data: count } = useDoctorPendingCount();
  const navigate = useNavigate();
  return (
    <button
      className="relative p-1.5 rounded-md hover:bg-muted transition-colors"
      onClick={() => navigate("/doctor/reports")}
      aria-label="Report notifications"
    >
      <Bell className="h-4 w-4" />
      {count != null && count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-medium">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </button>
  );
}

export function Layout({ children }: LayoutProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const user = session?.user;
  const role = (user as { role?: string } | undefined)?.role as
    | UserRole
    | undefined;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  const close = () => setSidebarOpen(false);

  const isStaff =
    role === UserRole.Admin ||
    role === UserRole.Manager ||
    role === UserRole.Doctor;
  const isOwnerOrManager = role === UserRole.Admin || role === UserRole.Manager;
  const isOwner = role === UserRole.Admin;
  const isPatient = role === UserRole.Patient;
  const isAuditor = role === UserRole.Auditor;

  const sidebar = (
    <>
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <img src={logoSrc} alt={appName} className="h-8 w-auto" />
          {role === UserRole.Doctor && <BellIcon />}
        </div>
        {user?.hospital && (
          <p className="text-xs text-muted-foreground mt-2 truncate">
            {getLocalizedName(user.hospital, i18n.language)}
          </p>
        )}
        {user && (
          <p className="text-sm text-foreground font-medium mt-1 truncate">
            {user.name}
          </p>
        )}
      </div>

      <nav className="flex-1 p-4 space-y-1 overflow-y-auto overflow-x-hidden">
        {isPatient ? (
          <Link to="/portal" onClick={close}>
            <Button variant="ghost" className="w-full justify-start gap-2">
              <FileText className="h-4 w-4" />
              {t("nav.myRecords")}
            </Button>
          </Link>
        ) : isAuditor ? (
          <>
            <Link to="/auditor" onClick={close}>
              <Button variant="ghost" className="w-full justify-start gap-2">
                <ShieldCheck className="h-4 w-4" />
                {t("audit.dashboard")}
              </Button>
            </Link>
            <Link to="/admin/audit-log" onClick={close}>
              <Button variant="ghost" className="w-full justify-start gap-2">
                <ClipboardCheck className="h-4 w-4" />
                {t("nav.auditLog")}
              </Button>
            </Link>
          </>
        ) : (
          <>
            <Link to="/dashboard" onClick={close}>
              <Button variant="ghost" className="w-full justify-start gap-2">
                <LayoutDashboard className="h-4 w-4" />
                {t("nav.dashboard")}
              </Button>
            </Link>
            <Link to="/consultation/new" onClick={close}>
              <Button variant="ghost" className="w-full justify-start gap-2">
                <Plus className="h-4 w-4" />
                {t("nav.newConsultation")}
              </Button>
            </Link>
            {isStaff && (
              <>
                <Link to="/patients" onClick={close}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                  >
                    <UserSquare2 className="h-4 w-4" />
                    {t("nav.patients")}
                  </Button>
                </Link>
                <Link to="/patients?add=1" onClick={close}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-muted-foreground"
                  >
                    <Plus className="h-4 w-4" />
                    {t("patients.addPatient")}
                  </Button>
                </Link>
              </>
            )}
            {role === UserRole.Doctor && (
              <Link to="/doctor/reports" onClick={close}>
                <Button variant="ghost" className="w-full justify-start gap-2">
                  <ClipboardSignature className="h-4 w-4" />
                  {t("doctorReports.navLink")}
                </Button>
              </Link>
            )}
          </>
        )}

        {isOwnerOrManager && (
          <>
            <div className="pt-4 pb-1 px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {t("nav.admin")}
            </div>
            <Link to="/admin/users" onClick={close}>
              <Button variant="ghost" className="w-full justify-start gap-2">
                <Users className="h-4 w-4" />
                {t("nav.users")}
              </Button>
            </Link>
            <Link to="/admin/departments" onClick={close}>
              <Button variant="ghost" className="w-full justify-start gap-2">
                <Building2 className="h-4 w-4" />
                {t("nav.departments")}
              </Button>
            </Link>
            {isOwner && (
              <>
                <Link to="/templates" onClick={close}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                  >
                    <ClipboardList className="h-4 w-4" />
                    {t("nav.templates")}
                  </Button>
                </Link>
                <Link to="/admin/report-structures" onClick={close}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                  >
                    <LayoutList className="h-4 w-4" />
                    {t("nav.reportStructures")}
                  </Button>
                </Link>
                <Link to="/admin/prompts" onClick={close}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                  >
                    <NotebookTabs className="h-4 w-4" />
                    {t("nav.prompts")}
                  </Button>
                </Link>
                <Link to="/admin/drugs" onClick={close}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2"
                  >
                    <Pill className="h-4 w-4" />
                    {t("nav.drugs")}
                  </Button>
                </Link>
              </>
            )}
            <Link to="/admin/audit-log" onClick={close}>
              <Button variant="ghost" className="w-full justify-start gap-2">
                <ClipboardCheck className="h-4 w-4" />
                {t("nav.auditLog")}
              </Button>
            </Link>
            <Link to="/auditor" onClick={close}>
              <Button variant="ghost" className="w-full justify-start gap-2">
                <ShieldCheck className="h-4 w-4" />
                {t("audit.dashboard")}
              </Button>
            </Link>
          </>
        )}

        {isOwner && (
          <Link to="/facility" onClick={close}>
            <Button variant="ghost" className="w-full justify-start gap-2">
              <Settings className="h-4 w-4" />
              {t("nav.facility")}
            </Button>
          </Link>
        )}
      </nav>

      <div className="p-4 border-t space-y-1">
        <a
          href="/admin/logs"
          target="_blank"
          rel="noopener noreferrer"
          onClick={close}
        >
          <Button variant="ghost" className="w-full justify-start gap-2">
            <Terminal className="h-4 w-4" />
            System Logs
          </Button>
        </a>
        <Link to="/profile" onClick={close}>
          <Button variant="ghost" className="w-full justify-start gap-2">
            <User className="h-4 w-4" />
            {t("nav.profile")}
          </Button>
        </Link>
        <Button
          variant="ghost"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          {t("common.logout")}
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-screen overflow-hidden bg-background flex">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={close}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 border-r bg-card flex flex-col overflow-hidden
          transition-transform duration-200
          md:static md:translate-x-0 md:z-auto md:shrink-0
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        <button
          className="absolute top-4 right-4 md:hidden text-muted-foreground"
          onClick={close}
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
        {sidebar}
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center gap-3 px-4 py-3 border-b bg-card sticky top-0 z-30">
          <button onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
          <img src={logoSrc} alt={appName} className="h-7 w-auto flex-1" />
          {role === UserRole.Doctor && <BellIcon />}
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-4 pb-8 md:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}
